import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Avatar, AvatarGroup, Box, Drawer, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import type { Trip, TripBuddy } from '../../../../../types/trip.types';
import { useGetIdeasQuery } from '../../../../../store/services/ideasApi';
import { useGetTripsQuery } from '../../../../../store/services/tripsApi';
import { formatTripDuration, formatDateRangeWithYear } from '../../../../../utils/dateUtils';
import { buildAvatarUrl, generateInitials, generateInitialsFromFullName } from '../../../../../utils/avatarUtils';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import CustomInput from '../../../../../common/CustomInput';
import { DateIcon, MarkerPinIcon } from '../../../../../components/Icons';
import TripMock1 from '../../../../../assets/images/TripMock1.jpg';
import tripMockDetailsFallback from '../../../../../assets/images/TripMockDetails.jpg';

import styles from '../SaveFlightModal/SaveFlightModal.module.scss';
import { Idea } from '../../../../../types/idea.types';

export type SaveTab = 'ideas' | 'trips';

export interface ChooseWhereToSavePayload {
  tab: SaveTab;
  ideaId: number | null;
  tripId: number | null;
  tripStopStartDateTime?: string;
  tripStopEndDateTime?: string;
}

export interface ChooseWhereToSaveModalProps {
  open: boolean;
  onClose: () => void;
  titleId: string;
  searchInputName: string;
  saveDisabled?: boolean;
  onSave: (payload: ChooseWhereToSavePayload) => void | Promise<void>;
  isSaving?: boolean;
  disableFooterWhileSaving?: boolean;
  noIdeaListsEmptyMessage?: string;
  errorText?: string | null;
  onErrorDismiss?: () => void;
  onCreateNewIdeaList?: () => void;
  onCreateNewTrip?: () => void;
  createNewIdeaListDisabled?: boolean;
  onTripSelect?: (tripId: number) => void;
  ideasOnly?: boolean;
}

const isTripActive = (trip: Trip): boolean => {
  if (!trip.start_date || !trip.end_date) return false;
  const now = new Date();
  const start = new Date(trip.start_date);
  const end = new Date(trip.end_date);
  return start <= now && end >= now;
};

const ChooseWhereToSaveModal: FC<ChooseWhereToSaveModalProps> = ({
  open,
  onClose,
  titleId,
  searchInputName,
  saveDisabled = false,
  onSave,
  isSaving = false,
  disableFooterWhileSaving = false,
  noIdeaListsEmptyMessage = 'You have no idea lists yet.',
  errorText,
  onErrorDismiss,
  onCreateNewIdeaList,
  onCreateNewTrip,
  createNewIdeaListDisabled = false,
  onTripSelect,
  ideasOnly = false,
}) => {
  const [tab, setTab] = useState<SaveTab>('ideas');
  const [listSearch, setListSearch] = useState('');
  const [selectedIdeaId, setSelectedIdeaId] = useState<number | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const { data: ideas = [] } = useGetIdeasQuery({});
  const { data: trips = [] } = useGetTripsQuery({}, { skip: ideasOnly });

  useEffect(() => {
    if (!open) {
      setListSearch('');
      setSelectedIdeaId(null);
      setSelectedTripId(null);
      setTab('ideas');
    }
  }, [open]);

  useEffect(() => {
    setSelectedIdeaId(null);
    setSelectedTripId(null);
  }, [tab]);

  const filteredIdeas = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return ideas;
    return ideas.filter(i => i.name.toLowerCase().includes(q));
  }, [ideas, listSearch]);

  const filteredTrips = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return trips;
    return trips.filter(t => t.name.toLowerCase().includes(q));
  }, [trips, listSearch]);

  const handleSaveClick = () => {
    void onSave({
      tab: activeTab,
      ideaId: selectedIdeaId,
      tripId: selectedTripId,
    });
  };

  const ideaThumb = (idea: Idea) => {
    return idea.custom_cover ? idea.custom_cover : TripMock1;
  };

  const renderTripBuddies = (buddies: TripBuddy[] | undefined) => {
    const list = buddies ?? [];
    if (list.length === 0) return null;

    return (
      <AvatarGroup
        max={4}
        className={styles.avatarGroup}
      >
        {list.map(buddy => {
          const avatarUrl = buildAvatarUrl(buddy.profile_picture);
          const hasSplitOrLogin = Boolean(
            buddy.first_name?.trim() || buddy.last_name?.trim() || buddy.email?.trim() || buddy.username?.trim()
          );
          const hasFullName = Boolean(buddy.full_name?.trim());
          const initials = hasSplitOrLogin
            ? generateInitials(buddy.first_name, buddy.last_name, buddy.email, buddy.username)
            : hasFullName
              ? generateInitialsFromFullName(buddy.full_name, buddy.email)
              : `U${String(buddy.id).slice(-1)}`;

          return (
            <Avatar
              key={buddy.id}
              sx={{
                backgroundColor: avatarUrl ? 'transparent' : '#e3f2fd',
                color: avatarUrl ? 'transparent' : '#0098f0',
                fontSize: '10px',
                fontWeight: 600,
              }}
              src={avatarUrl || undefined}
              slotProps={{
                img: {
                  style: {
                    objectFit: 'cover',
                    objectPosition: 'center',
                  },
                },
              }}
            >
              {!avatarUrl && initials}
            </Avatar>
          );
        })}
      </AvatarGroup>
    );
  };

  const backdropSx = { backgroundColor: 'rgba(34, 43, 58, 0.35)' };
  const activeTab: SaveTab = ideasOnly ? 'ideas' : tab;
  const saveButtonDisabled =
    saveDisabled || isSaving || (activeTab === 'ideas' ? selectedIdeaId == null : selectedTripId == null);
  const footerLocked = disableFooterWhileSaving && isSaving;
  const dialogContent = (
    <>
      <Box className={styles.header}>
        <Typography
          id={titleId}
          variant="h5Bold"
          className={styles.title}
        >
          Choose Where to Save
        </Typography>
        <IconButton
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {!ideasOnly ? (
        <Box className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${tab === 'ideas' ? styles.tabActive : ''}`}
            onClick={() => setTab('ideas')}
          >
            Save to Ideas
          </button>
          <button
            type="button"
            className={`${styles.tab} ${tab === 'trips' ? styles.tabActive : ''}`}
            onClick={() => setTab('trips')}
          >
            Save to Trips
          </button>
        </Box>
      ) : null}

      <Box className={styles.scrollBody}>
        <Box className={styles.listPanelTop}>
          <Typography
            variant="h9Bold"
            className={styles.sectionLabel}
          >
            {activeTab === 'ideas' ? 'Ideas' : 'Trips'}
          </Typography>

          <CustomInput
            inputName={searchInputName}
            inputLabel=""
            placeholder="Search"
            value={listSearch}
            onChange={e => setListSearch(e.target.value)}
            customClass={styles.search}
            startIcon={<SearchIcon sx={{ color: '#5e6a81', fontSize: 22 }} />}
          />
        </Box>

        <Box className={styles.listScroll}>
          {activeTab === 'ideas' ? (
            <Box className={styles.list}>
              {filteredIdeas.length === 0 ? (
                <Typography
                  component="p"
                  className={styles.metaText}
                  sx={{ py: 2 }}
                >
                  {ideas.length === 0 ? noIdeaListsEmptyMessage : 'No lists match your search.'}
                </Typography>
              ) : (
                filteredIdeas.map(idea => (
                  <button
                    key={idea.id}
                    type="button"
                    className={`${styles.ideaRow} ${selectedIdeaId === idea.id ? styles.ideaRowSelected : ''}`}
                    onClick={() => setSelectedIdeaId(idea.id)}
                  >
                    <Box
                      className={styles.thumb}
                      sx={{ backgroundImage: `url(${ideaThumb(idea)})` }}
                    />
                    <Box className={styles.body}>
                      <p className={styles.cardTitle}>{idea.name}</p>
                      <Box className={styles.meta}>
                        <Box className={styles.metaLine}>
                          <MarkerPinIcon size={16} />
                          <span className={styles.metaText}>
                            {idea.stops_count} {idea.stops_count === 1 ? 'Stop' : 'Stops'}
                          </span>
                        </Box>
                        <Box className={styles.metaLine}>
                          <DateIcon size={16} />
                          <span className={styles.metaText}>
                            {idea.duration_in_days >= 0
                              ? `${idea.duration_in_days} ${idea.duration_in_days === 1 ? 'day' : 'days'}`
                              : '—'}
                          </span>
                        </Box>
                      </Box>
                    </Box>
                  </button>
                ))
              )}
            </Box>
          ) : (
            <Box className={styles.list}>
              {filteredTrips.length === 0 ? (
                <Typography
                  component="p"
                  className={styles.metaText}
                  sx={{ py: 2 }}
                >
                  No trips match your search.
                </Typography>
              ) : (
                filteredTrips.map((trip: Trip) => {
                  const active = isTripActive(trip);
                  const destinations = trip.destination_countries?.map(c => c.name).join(' - ') || '—';

                  return (
                    <button
                      key={trip.id}
                      type="button"
                      className={`${styles.ideaRow} ${selectedTripId === trip.id ? styles.ideaRowSelected : ''}`}
                      onClick={() => {
                        setSelectedTripId(trip.id);
                        onTripSelect?.(trip.id);
                      }}
                    >
                      <Box
                        className={styles.thumb}
                        sx={{
                          backgroundImage: `url(${trip.custom_cover || tripMockDetailsFallback})`,
                        }}
                      />
                      <Box className={styles.tripContent}>
                        <Box className={styles.tripTitleRow}>
                          <p className={styles.tripTitleText}>{trip.name}</p>
                          {trip.start_date && trip.end_date && !isMobile ? (
                            <span className={styles.durationMuted}>
                              · {formatTripDuration(trip.start_date, trip.end_date)}
                            </span>
                          ) : null}
                          {active ? (
                            <Box className={styles.activeChip}>
                              <span className={styles.activeChipText}>Active</span>
                            </Box>
                          ) : null}
                        </Box>
                        <Box className={styles.tripDetailsCol}>
                          <Box className={styles.metaLine}>
                            <DateIcon size={16} />
                            <span className={styles.metaText}>
                              {trip.start_date && trip.end_date
                                ? formatDateRangeWithYear(trip.start_date, trip.end_date)
                                : '—'}
                            </span>
                          </Box>
                          <Box className={styles.tripLocationRow}>
                            <Box className={styles.metaLine}>
                              <MarkerPinIcon size={16} />
                              <span className={styles.metaText}>{destinations}</span>
                            </Box>
                            {renderTripBuddies(trip.buddies)}
                          </Box>
                        </Box>
                      </Box>
                    </button>
                  );
                })
              )}
            </Box>
          )}
        </Box>

        {errorText ? (
          <Alert
            severity="error"
            onClose={onErrorDismiss}
            sx={{ mt: 1, flexShrink: 0 }}
          >
            {errorText}
          </Alert>
        ) : null}
      </Box>

      <Box className={styles.footerStack}>
        {activeTab === 'ideas' && onCreateNewIdeaList ? (
          <Box className={styles.footerCreateRow}>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={onCreateNewIdeaList}
              disabled={footerLocked || createNewIdeaListDisabled}
            >
              + Create new idea list
            </button>
          </Box>
        ) : null}
        {!ideasOnly && tab === 'trips' && onCreateNewTrip ? (
          <Box className={styles.footerCreateRow}>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={onCreateNewTrip}
              disabled={footerLocked}
            >
              + Create new trip
            </button>
          </Box>
        ) : null}

        <Box className={styles.footer}>
          <CustomButton
            variant="outlined"
            btnType="button"
            onClick={onClose}
            disabled={footerLocked}
            customClass={styles.footerBtn}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            btnType="button"
            onClick={handleSaveClick}
            disabled={saveButtonDisabled}
            customClass={styles.footerBtn}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </CustomButton>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          slotProps={{
            backdrop: { sx: backdropSx },
          }}
          PaperProps={{
            className: styles.drawerPaper,
            elevation: 8,
          }}
        >
          {dialogContent}
        </Drawer>
      ) : (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby={titleId}
          sx={{
            '& .MuiBackdrop-root': backdropSx,
          }}
        >
          <Box className={styles.modalPaper}>{dialogContent}</Box>
        </Modal>
      )}
    </>
  );
};

export default ChooseWhereToSaveModal;
